import { ProductionCardStatus } from '@app/models/production/production-card';
import { ProductionCardBundle, ProductionCardBundleStatus } from '@app/models/production/production-card/production-card-bundle';
import _ from 'lodash';

export type BatchingDetailsViewMode = 'bundles' | 'transfer-units-view' | 'transfer-units' | null;
export type BatchingCardViewMode = 'events' | 'bundles' | null;

export class ProductionCardStatusUtil {
  public static planningStatuses = [ProductionCardStatus.Draft, ProductionCardStatus.Submitted, ProductionCardStatus.Approved];

  public static preBatchingStatuses = [ProductionCardStatus.PlanningForBatching, ProductionCardStatus.InRouteToBatching];

  public static batchingStatuses = [ProductionCardStatus.Batching, ProductionCardStatus.InRouteToBrd];

  public static brdStatuses = [ProductionCardStatus.Brd];

  public static dyeingStatuses = [ProductionCardStatus.Dyeing, ProductionCardStatus.ReadyForFinishing];

  public static finishingStatuses = [ProductionCardStatus.Finishing];

  public static voidedStatuses = [ProductionCardStatus.Deleted, ProductionCardStatus.Rejected];

  public static completedStatuses = [ProductionCardStatus.FinishedGood, ProductionCardStatus.Shipped, ProductionCardStatus.Closed, ProductionCardStatus.Reassignment];

  public static allValidStatuses = [
    ...this.planningStatuses,
    ...this.batchingStatuses,
    ...this.brdStatuses,
    ...this.dyeingStatuses,
    ...this.finishingStatuses,
    ...this.completedStatuses,
  ];

  public static getValidNextStatuses(currentStatus: ProductionCardStatus): number[] {
    switch (currentStatus) {
      case ProductionCardStatus.Draft: // Draft
        return [ProductionCardStatus.Submitted, ProductionCardStatus.Deleted, ProductionCardStatus.Rejected]; // Submitted, Deleted, Rejected
      case ProductionCardStatus.Submitted: // Submitted
        return [ProductionCardStatus.Approved, ProductionCardStatus.Deleted, ProductionCardStatus.Rejected]; // Approved, Deleted, Rejected
      case ProductionCardStatus.Approved: // Approved
        return [ProductionCardStatus.PlanningForBatching, ProductionCardStatus.Deleted]; // PlanningForBatching, Deleted
      case ProductionCardStatus.PlanningForBatching: // PlanningForBatching
        return [ProductionCardStatus.InRouteToBatching, ProductionCardStatus.Deleted]; // InRouteToBatching, Deleted
      case ProductionCardStatus.InRouteToBatching: // InRouteToBatching
        return [ProductionCardStatus.Batching]; // Batching, Deleted
      case ProductionCardStatus.Batching: // Batching
        return [ProductionCardStatus.InRouteToBrd]; // InRouteToBrd, Deleted
      case ProductionCardStatus.InRouteToBrd: // InRouteToBrd
        return [ProductionCardStatus.Brd]; // Brd, Deleted
      case ProductionCardStatus.ReadyForFinishing: // Dyeing
        return [ProductionCardStatus.Finishing];
      default:
        return [];
    }
  }

  //#region Status Checkers

  //#region Batching and Bundle View Modes
  private static ShowBundlesCard(status: ProductionCardStatus): boolean {
    const validStatuses = [
      ...this.preBatchingStatuses,
      ...this.batchingStatuses,
      ...this.brdStatuses,
      ...this.dyeingStatuses,
      ...this.finishingStatuses,
      ...this.completedStatuses,
    ];
    return validStatuses.includes(status);
  }

  public static ShowFinishingRouteCard(status: ProductionCardStatus): boolean {
    const validStatuses = [...this.finishingStatuses, ...this.completedStatuses];
    return validStatuses.includes(status);
  }

  public static BatchingCardViewMode(
    productionCardBundles: ProductionCardBundle[],
    status: ProductionCardStatus,
    reprocessNumber?: number,
  ): BatchingCardViewMode {
    if (!this.ShowBundlesCard(status)) {
      return null;
    }

    // For reprocess production cards (reprocessNumber > 0)
    // Hide bundles card if no bundles exist AND status is past batching/BRD stages
    // (meaning it skipped bundles entirely and went straight to finishing)
    if (reprocessNumber && reprocessNumber > 0 && _.isEmpty(productionCardBundles)) {
      const postBatchingStatuses = [...this.dyeingStatuses, ...this.finishingStatuses, ...this.completedStatuses];
      if (postBatchingStatuses.includes(status)) {
        return null;
      }
    }

    if (_.isEmpty(productionCardBundles)) {
      return 'events';
    }

    return 'bundles';
  }

  public static BatchingDetailsViewMode(
    productionCardBundles: ProductionCardBundle[],
    status: ProductionCardStatus,
  ): BatchingDetailsViewMode {
    const transferUnitStatuses = [...this.preBatchingStatuses, ProductionCardStatus.Batching];

    if (!this.ShowBundlesCard(status)) {
      return null;
    }

    if (transferUnitStatuses.includes(status) && _.isEmpty(productionCardBundles)) {
      return 'transfer-units';
    }

    return 'bundles';
  }

  private static readonly excludedBundleStatuses = [ProductionCardBundleStatus.Used, ProductionCardBundleStatus.Removed];

  public static getActiveBundles(productionCardBundles: ProductionCardBundle[]): ProductionCardBundle[] {
    return productionCardBundles.filter((b) => !this.excludedBundleStatuses.includes(b.status));
  }

  public static ShowCreateBundlesButton(
    productionCardBundles: ProductionCardBundle[],
    status: ProductionCardStatus,
    numberOfPorts?: number,
  ): boolean {
    if (status !== ProductionCardStatus.Batching) {
      return false;
    }

    const activeBundles = this.getActiveBundles(productionCardBundles);

    // If no active bundles exist, show button
    if (_.isEmpty(activeBundles)) {
      return true;
    }

    // If numberOfPorts is provided, show button if there are fewer active bundles than ports
    if (numberOfPorts && activeBundles.length < numberOfPorts) {
      return true;
    }

    return false;
  }

  public static CanEditBatching(status: ProductionCardStatus): boolean {
    const editStatus = [ProductionCardStatus.PlanningForBatching, ProductionCardStatus.Batching];
    return editStatus.includes(status);
  }

  //#endregion

  public static inBatching(status: ProductionCardStatus): boolean {
    const validStatuses = [
      ProductionCardStatus.Batching,
      ProductionCardStatus.InRouteToBrd,
      ProductionCardStatus.Brd,
      ProductionCardStatus.Dyeing,
    ];
    return validStatuses.includes(status);
  }

  public static planningForBatching(status: ProductionCardStatus): boolean {
    return status === ProductionCardStatus.PlanningForBatching;
  }

  public static editableStatuses = [
    ProductionCardStatus.Draft,
    ProductionCardStatus.Submitted,
    ProductionCardStatus.Approved,
    ProductionCardStatus.PlanningForBatching,
    ProductionCardStatus.Deleted,
    ProductionCardStatus.Rejected,
  ];

  public static CanEdit(status: ProductionCardStatus): boolean {
    return this.editableStatuses.includes(status);
  }
}
